import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { openPortal } from "@/lib/billing";
import { AuthShell } from "../auth/AuthShell";

/** Account / billing — reads the profile from /api/me via the auth context. */
export function AccountScreen() {
  const { me, user, signOut } = useAuth();
  const [busy, setBusy] = useState(false);

  async function manageSubscription() {
    setBusy(true);
    const ok = await openPortal();
    if (!ok) window.location.href = "mailto:info@earlywire.io?subject=Billing";
    setBusy(false);
  }

  return (
    <AuthShell title="OPERATOR ACCOUNT" subtitle="Profile & subscription">
      <div className="acct-grid">
        <Field k="Email" v={me?.contactEmail ?? user?.email ?? me?.email ?? "—"} />
        <Field k="Display name" v={me?.displayName ?? user?.displayName ?? "—"} />
        <Field k="Plan" v={(me?.plan ?? "—").toUpperCase()} accent />
        <Field k="Status" v={(me?.status ?? "—").toUpperCase()} accent />
        <Field
          k="Member since"
          v={me?.createdAt ? new Date(me.createdAt).toLocaleDateString() : "—"}
        />
        <Field k="Auth provider" v={user?.providerData?.[0]?.providerId ?? "—"} />
      </div>

      <div className="acct-billing">
        <div className="ph-label">Billing</div>
        {me?.plan === "internal" ? (
          <p className="auth-copy">Internal account — full API access, never billed.</p>
        ) : me?.plan === "press" || me?.plan === "creator" ? (
          <p className="auth-copy">
            Complimentary {me.plan} account — full desk access, never billed. Enjoy, and tell your
            audience where the alerts come from.
          </p>
        ) : me?.subscriptionStatus ? (
          <>
            <p className="auth-copy">
              Subscription {String(me.subscriptionStatus)}
              {me?.currentPeriodEnd ? ` · renews ${new Date(Number(me.currentPeriodEnd)).toLocaleDateString()}` : ""}.
              Cancel, change card or download invoices in the billing portal.
            </p>
            <button className="auth-btn" disabled={busy} onClick={() => void manageSubscription()}>
              {busy ? "OPENING…" : "MANAGE SUBSCRIPTION"}
            </button>
          </>
        ) : me?.compUntil && me.compUntil > Date.now() ? (
          <p className="auth-copy">
            Complimentary access until <b>{new Date(me.compUntil).toLocaleDateString()}</b> — enjoy.
            A seat keeps the desk after that.
          </p>
        ) : (
          <p className="auth-copy">No subscription yet — your seat activates at checkout.</p>
        )}
      </div>

      <div className="acct-actions">
        <Link className="auth-btn" to="/ops">
          ← BACK TO OPS
        </Link>
        <Link className="auth-btn primary" to="/delivery">
          ALERT DELIVERY
        </Link>
        <button className="auth-btn" onClick={() => void signOut()}>
          SIGN OUT
        </button>
      </div>
    </AuthShell>
  );
}

function Field({ k, v, accent }: { k: string; v: string; accent?: boolean }) {
  return (
    <div className="acct-cell">
      <div className="k">{k}</div>
      <div className="v" style={accent ? { color: "var(--green)" } : undefined}>
        {v}
      </div>
    </div>
  );
}
